// src/pages/admin/Posts/PostViews.js
import React, { useState, useEffect } from 'react';
import { Table, Input, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

const PostViews = ({ onLogout }) => { // Added onLogout prop
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Initial posts data (replace with API fetch in production)
  const initialPosts = [
    { id: 1, title: 'AI Revolution in Africa', date: '2025-02-10', tags: ['Artificial Intelligence'], views: 1200 },
    { id: 2, title: 'Future of Cybersecurity', date: '2025-01-28', tags: ['Cybersecurity'], views: 950 },
    { id: 3, title: 'Big Data and You', date: '2024-12-15', tags: ['Data Science'], views: 870 },
    { id: 4, title: 'Machine Learning in Healthcare', date: '2024-11-20', tags: ['Artificial Intelligence'], views: 1350 },
    { id: 5, title: 'Cyber Threats to Watch', date: '2024-10-10', tags: ['Cybersecurity'], views: 1100 },
    { id: 6, title: 'AI for Social Good', date: '2025-02-18', tags: ['Artificial Intelligence'], views: 1300 },
    { id: 7, title: 'The Role of AI in Climate Change', date: '2025-02-05', tags: ['Artificial Intelligence', 'Environment'], views: 1050 },
    { id: 8, title: 'AI Ethics and Privacy Concerns', date: '2025-01-15', tags: ['Artificial Intelligence', 'Ethics'], views: 1120 },
    { id: 9, title: 'Cybersecurity in the Age of IoT', date: '2024-12-25', tags: ['Cybersecurity', 'IoT'], views: 1020 },
    { id: 10, title: 'The Future of Quantum Computing in Data Science', date: '2024-12-05', tags: ['Data Science', 'Quantum Computing'], views: 950 },
    { id: 11, title: 'Machine Learning for Financial Services', date: '2024-11-30', tags: ['Machine Learning', 'Finance'], views: 1080 },
    { id: 12, title: '5G and Cybersecurity: What You Need to Know', date: '2024-11-10', tags: ['Cybersecurity', '5G'], views: 940 },
    { id: 13, title: 'The Rise of Edge AI', date: '2025-03-02', tags: ['Artificial Intelligence', 'IoT'], views: 1250 },
    { id: 14, title: 'Deep Learning Breakthroughs in 2025', date: '2025-03-10', tags: ['Deep Learning', 'AI'], views: 1450 },
    { id: 15, title: 'Cybersecurity Threats in 2025', date: '2025-04-05', tags: ['Cybersecurity', 'Threats'], views: 980 },
    { id: 16, title: 'Data Science in Sports Analytics', date: '2025-04-15', tags: ['Data Science', 'Sports'], views: 890 },
    { id: 17, title: 'How AI is Powering Smart Cities', date: '2025-05-01', tags: ['Artificial Intelligence', 'Smart Cities'], views: 1100 },
    { id: 18, title: 'AI-Powered Assistants: Future of Work', date: '2025-05-20', tags: ['Artificial Intelligence', 'Workplace'], views: 1180 },
    { id: 19, title: 'AI in Education: The New Era of Learning', date: '2025-06-10', tags: ['Artificial Intelligence', 'Education'], views: 1320 },
    { id: 20, title: 'The Blockchain-Cybersecurity Connection', date: '2025-06-25', tags: ['Cybersecurity', 'Blockchain'], views: 950 },
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      setPosts(initialPosts);
      setFilteredPosts(initialPosts);
      setLoading(false);
    }, 500);

    // Placeholder for real API call
    /*
    fetch('/api/post-views')
      .then((response) => response.json())
      .then((data) => {
        setPosts(data);
        setFilteredPosts(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching post views:', error);
        setLoading(false);
      });
    */
  }, []);

  const handleSearch = (value) => {
    const filtered = posts.filter((post) =>
      post.title.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredPosts(filtered);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field && sorter.order) {
      const sorted = [...filteredPosts].sort((a, b) => {
        const valueA = sorter.field === 'views' ? a[sorter.field] : a[sorter.field].toString().toLowerCase();
        const valueB = sorter.field === 'views' ? b[sorter.field] : b[sorter.field].toString().toLowerCase();
        return sorter.order === 'ascend' ? (typeof valueA === 'number' ? valueA - valueB : valueA.localeCompare(valueB)) : (typeof valueA === 'number' ? valueB - valueA : valueB.localeCompare(valueA));
      });
      setFilteredPosts(sorted);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => tags.join(', '),
    },
    {
      title: 'Views',
      dataIndex: 'views',
      key: 'views',
      sorter: true,
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
      <Title level={2} style={{ marginBottom: 16 }}>Post Views</Title>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Input
          placeholder="Search posts by title"
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={filteredPosts}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: filteredPosts.length,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />
    </div>
  );
};

export default PostViews;